<template>
  <div class="row-editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <img
          :src="moduleConfigs.icon"
          alt="" />
        <div>{{ moduleConfigs.name }}</div>
      </div>
      <Tabs
        v-model="inputSectionTab"
        :tabs="tabs"
        :is-objects="true"
        @input="scrollToSection" />
    </div>

    <div
      class="editor"
      @scroll="throttleScroll">
      <div
        id="display-label"
        class="editor-section">
        <h2>Display label</h2>
        <div class="body">
          <b-field
            label="Display Label"
            class="input-field"
            message="">
            <PivotLabelInput
              v-model="inputs.name"
              :has-pivot="!!context.pivotDimension"
              :pivot-label="context.pivotDimension?.name || ''" />
          </b-field>
        </div>
      </div>
      <div
        id="row-style"
        class="editor-section">
        <h2>Row Style</h2>
        <div class="body">
          <div class="m-0">
            <b-field
              label="Row Type"
              class="input-field"
              message="">
              <b-select
                v-model="inputs.style.row_type"
                placeholder="Select a row type"
                expanded
                style="width: 100%">
                <option
                  v-for="option in rowStyleTypesOptions"
                  :key="option"
                  :value="option">
                  {{ option }}
                </option>
              </b-select>
            </b-field>

            <b-field class="input-field">
              <b-switch
                v-model="inputs.style.add_divider"
                class="mt-3"
                type="is-info">
                Add Divider
              </b-switch>
            </b-field>
          </div>
        </div>
      </div>
      <div
        id="measure"
        class="editor-section">
        <h2>Measure</h2>
        <div class="body">
          <MeasureConfigEditor
            v-model="inputs.measure_config"
            :errors="childErrors('measure_config', errors)"
            :context="{
              reportDimensions,
              timeRangeDefaultLabel: context.timeRangeDefaultLabel,
              timeRangeDefaultName: context.timeRangeDefaultName,
            }" />
        </div>
      </div>

      <div
        id="benchmark"
        class="editor-section">
        <h2>Benchmark</h2>
        <div class="body">
          <BenchmarkConfigEditor
            v-model="inputs.comparison_config"
            :errors="childErrors('comparison_config', errors)"
            :context="{
              parentMeasure: benchmarkParentMeasure,
              parentIsPctTotal: benchmarkParentIsPctTotal,
              reportDimensions,
              timeRangeDefaultLabel: 'Use measure default',
              timeRangeDefaultName: benchmarkTimeRangeName,
            }" />
        </div>
      </div>

      <div
        id="format"
        class="editor-section">
        <h2>Format</h2>
        <div class="body">
          <DataFormatEditor
            v-model="inputs.data_format"
            :errors="childErrors('data_format', errors)"
            :measure="inputs.measure_config?.measure"
            :type-override="formatTypeOverride" />
        </div>
      </div>

      <div
        v-if="hasTotalColumns"
        id="total-columns"
        class="editor-section">
        <h2>Total Column</h2>
        <div class="body">
          <TrackerRowTotals
            v-model="inputs.tracker_row_totals"
            :errors="childErrors('tracker_row_totals', errors)"
            :context="{
              ...context,
              parentRowMeasure: rowMeasure,
              parentRowTimeRange: measureTimeRange,
              parentRowDataFormat: inputs.data_format,
              parentRowFilters: inputs.measure_config?.filters || [],
              rowMeasureTimeRange: measureTimeRange,
            }"
            class="sub-section" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import TrackerRowTotals from '@/components/pdf_report/tracker_table_module/TrackerRowTotals.vue'
  import MeasureConfigEditor from '@/components/reports/config/modules/common/MeasureConfigEditor'
  import BenchmarkConfigEditor from '@/components/reports/config/modules/common/BenchmarkConfigEditor'
  import Tabs from '@/components/pdf_report/Tabs.vue'
  import { throttle } from 'lodash'
  import PivotLabelInput from '@/components/pdf_report/PivotLabelInput.vue'
  import DataFormatEditor from '@/components/reports/config/modules/common/DataFormatEditor'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  export default {
    name: 'TrackerRow',
    components: {
      TrackerRowTotals,
      MeasureConfigEditor,
      BenchmarkConfigEditor,
      Tabs,
      DataFormatEditor,
      PivotLabelInput,
    },
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      errors: {
        type: Array,
        default: () => [],
      },
      context: {
        type: Object,
        default: () => {},
      },
    },

    data () {
      return {
        nameInput: '',
        measureFilterToggle: false,
        rowStyleTypesOptions: ['Primary', 'Secondary',],

        moduleConfigs: {
          name: 'Tracker Row',
          type: 'TrackerTableRow',
          hideInLibrary: true,
          icon: require('@/assets/icons/tracker-row.svg'),
        },

        inputSectionTab: 'display-label',
      }
    },

    computed: {
      inputs: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
      throttleScroll () {
        return throttle(this.onScroll, 100)
      },
      rowMeasure () {
        return this.inputs.measure_config?.measure || null
      },
      reportDimensions () {
        const dims = []
        if (this.context.timeDimension) dims.push(this.context.timeDimension)
        if (this.context.pivotDimension) dims.push(this.context.pivotDimension)
        return dims
      },
      benchmarkParentMeasure () {
        return this.inputs.measure_config?.measure || null
      },
      benchmarkParentIsPctTotal () {
        return !!this.inputs.measure_config?.is_pct_total
      },
      benchmarkTimeRangeName () {
        return this.inputs.measure_config?.time_range?.name || this.context.timeRangeDefaultName || ''
      },
      measureTimeRange () {
        return this.inputs.measure_config?.time_range || null
      },
      formatTypeOverride () {
        if (this.inputs.measure_config?.is_pct_total) return 'percentage'
        if (this.inputs.comparison_config?.difference_type === 'var') return 'percentage'
        return null
      },
      hasTotalColumns () {
        return this.context?.trackerTotalHeaders?.length > 0
      },

      tabs () {
        const baseTabs = [
          {
            label: 'Display label',
            id: 'display-label',
            hasErrors: false,
          },
          {
            label: 'Row Style',
            id: 'row-style',
            hasErrors: this.inputs?.style?.errors,
          },
          {
            label: 'Measure',
            id: 'measure',
            hasErrors: _childErrors('measure_config', this.errors).length > 0,
          },
          {
            label: 'Benchmark',
            id: 'benchmark',
            hasErrors: _childErrors('comparison_config', this.errors).length > 0,
          },
          {
            label: 'Format',
            id: 'format',
            hasErrors: _childErrors('data_format', this.errors).length > 0,
          },
        ]
        if (this.hasTotalColumns) {
          baseTabs.push({
            label: 'Total Column',
            id: 'total-columns',
            hasErrors: _childErrors('tracker_row_totals', this.errors).length > 0,
          })
        }
        return baseTabs
      },
    },

    async mounted () {},

    beforeDestroy () {},

    methods: {
      childErrors: _childErrors,
      onScroll (event) {
        if (this._scrollingToSection) return
        const container = event.target
        const scrollTop = container.scrollTop
        const scrollingDown = scrollTop >= (this._lastScrollTop || 0)
        this._lastScrollTop = scrollTop
        const scrollableDivRect = container.getBoundingClientRect()
        const threshold = 10
        let activeSection = this.tabs[0].id
        for (const tab of this.tabs) {
          const el = container.querySelector(`#${tab.id}`)
          if (!el) continue
          if (el.getBoundingClientRect().top <= scrollableDivRect.top + threshold) {
            activeSection = tab.id
          }
        }
        const currentIndex = this.tabs.findIndex(t => t.id === this.inputSectionTab)
        const newIndex = this.tabs.findIndex(t => t.id === activeSection)
        if (scrollingDown && newIndex >= currentIndex) {
          this.inputSectionTab = activeSection
        } else if (!scrollingDown && newIndex <= currentIndex) {
          this.inputSectionTab = activeSection
        }
      },
      scrollToSection (sectionId) {
        this._scrollingToSection = true
        this.inputSectionTab = sectionId
        const container = this.$el.querySelector('.editor')
        const el = container && container.querySelector(`#${sectionId}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', })
        }
        clearTimeout(this._scrollTimer)
        this._scrollTimer = setTimeout(() => { this._scrollingToSection = false }, 400)
      },
    },
  }
</script>

<style lang="scss" scoped>
.editor-header {
  border-bottom: $border-1;
  padding: 20px;
  background: white;

  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 18px;
    align-items: center;
    margin-bottom: 15px;
    img {
      width: 35px;
      height: 35px;
      margin-right: 10px;
      border: $border-1;
      border-radius: 8px;
      padding: 5px;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
}

.row-editor-container {
  width: 100%;
  height: 100%;
  margin: auto !important;
  background: #fbfbfb;
  display: flex;
  flex-direction: column;
  padding: 0px;
}

.editor {
  padding: 20px;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: auto;
  flex-direction: column;

  .editor-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;

    h2 {
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 10px;
      text-transform: capitalize;
    }

    .body {
      border: $border-1;
      border-radius: 10px;
      padding: 18px;
      background: white;
    }
  }
}
</style>
